import {
  ASSIGN_QUESTIONS_TO_FORM_SECTION,
  CREATE_QUESTIONS_URLS,
  CREATE_SECTION_IN_FORM,
  DELETE_SECTION_URL,
  EDIT_QUESTIONS_URL,
  EDIT_SECTION_URL,
  SELECT_TYPES
} from "@/utils/constants/constants";
import alert_mixin from "@/utils/mixins/alert_mixin";
import formatting_mixins from "@/utils/mixins/formatting_mixins";
import axios from "axios";
import "datatables.net";
import "datatables.net-dt/css/dataTables.dataTables.css";
import Swal from "sweetalert2";

export default {
  name: "CreateEntity",
  mixins: [alert_mixin, formatting_mixins],
  methods: {
    edit_preview_question(question) {
      this.current_edit_question = JSON.parse(JSON.stringify(question));
      const raw_options = question.multi_select_choices || question.options || [];
      this.current_edit_question.options = raw_options.map(opt => {
        return typeof opt === 'object' ? opt : { choice: opt };
      });

      this.current_edit_question.show_option_inputs =
        ['multi_select', 'single_select'].includes(
          this.current_edit_question.question_type?.question_type || ''
        );
      this.current_edit_question.question_type_id = question.question_type?.id?.toString() || '';
      this.current_edit_question.data_point_id = question.data_point?.id || null;
      this.current_edit_question.selected_tag = question.tag?.id || null;
      this.current_edit_question.available_tags = this.get_tags_for_data_point(question.data_point?.id);

      // Set edit mode
      this.current_edit_mode = 'main_question';
      this.show_edit_modal = true;
    },

    async create_form_question(form) {
      const validation_error = this.validate_form(form);
      if (validation_error) return this.show_error(validation_error);

      const payload = this.build_payload(form);

      if (!this.validate_payload(payload)) return;

      try {
        await this.submit_question(payload, form);
      } catch (error) {
        this.handle_submission_error(error);
      }
    },


    validate_all_questions() {
      if (!this.section_id) {
        return "Please select a section before saving.";
      }

      for (const [form_index, form] of this.question_forms.entries()) {
        const question_index = form_index + 1;

        const main_error = this.validate_main_question(form, question_index);
        if (main_error) return main_error;

        const dependent_error = this.validate_dependent_questions(form, question_index);
        if (dependent_error) return dependent_error;
      }

      return null;
    },

    validate_main_question(form, question_index) {
      if (!form.question_text?.trim()) {
        return `Question ${question_index}: Question text is required.`;
      }

      if (!form.question_type_id) {
        return `Question ${question_index}: Question type is required.`;
      }

      const main_type = this.question_types.find(
        t => t.id === form.question_type_id
      );

      if (this.is_select_type(main_type)) {
        const valid_options = (form.option_inputs || [])
          .map(option => option.trim())
          .filter(Boolean);

        if (valid_options.length < 2) {
          return `Question ${question_index}: Select-type questions require at least 2 options.`;
        }
      }

      return null;
    },

    validate_dependent_questions(form, question_index) {
      if (!Array.isArray(form.dependant_questions)) {
        return null;
      }

      for (const [dep_index, dep_question] of form.dependant_questions.entries()) {
        const dependent_index = dep_index + 1;

        const error = this.validate_single_dependent_question(
          dep_question,
          question_index,
          dependent_index
        );

        if (error) return error;
      }

      return null;
    },

    validate_single_dependent_question(dep_question, question_index, dependent_index) {
      if (!dep_question.question_text?.trim()) {
        return `Question ${question_index}, Dependent ${dependent_index}: Question text is required.`;
      }

      if (!dep_question.question_type_id) {
        return `Question ${question_index}, Dependent ${dependent_index}: Question type is required.`;
      }

      if (!dep_question.trigger_condition?.toString().trim()) {
        return `Question ${question_index}, Dependent ${dependent_index}: Trigger condition is required.`;
      }

      const dependent_type = this.question_types.find(
        t => t.id === dep_question.question_type_id
      );

      if (this.is_select_type(dependent_type)) {
        const dependent_options = (dep_question.options || [])
          .map(option => option.choice?.trim())
          .filter(Boolean);

        if (dependent_options.length < 2) {
          return `Question ${question_index}, Dependent ${dependent_index}: Select-type dependent questions require at least 2 options.`;
        }
      }

      return null;
    },

    is_select_type(question_type) {
      return SELECT_TYPES.includes(question_type?.question_type);
    },






    async save_all_questions() {
      const validation_error = this.validate_all_questions();
      if (validation_error) {
        this.show_error(validation_error);
        return;
      }
      if (!this.section_id) {
        this.show_error("Please select a section before saving.");
        return;
      }

      for (const form of this.question_forms) {

        // VALIDATION
        if (!form.question_text?.trim()) {
          this.show_error("Each question must have text.");
          return;
        }

        if (!form.question_type_id) {
          this.show_error("Each question must have a type.");
          return;
        }

        const type = this.question_types.find(t => t.id === form.question_type_id);
        const is_select_type = SELECT_TYPES.includes(type?.question_type);

        const options = is_select_type
          ? form.option_inputs.filter(o => o.trim() !== "")
          : [];

        if (is_select_type && options.length < 2) {
          this.show_error("Select-type questions require at least 2 options.");
          return;
        }

        const payload = {
          question_text: form.question_text.trim(),
          question_description: form.question_description,
          question_type_id: form.question_type_id,
          project_id: this.project_id,
          is_mandatory: form.mandatory === "Yes",
          question_number: form.question_number || null,
          multi_select_options: options,
          section_id: this.section_id,
          data_point_id: form.data_point_id || null,
          tag_id: form.tag_id || null,
          dependant_questions: this.build_dependant_questions(form.dependant_questions)
        };

        try {
          const res = await axios.post(CREATE_QUESTIONS_URLS, payload, {
            headers: this.headers
          });

          const response = typeof res.data === "string" ? JSON.parse(res.data) : res.data;

          if (response.status?.toLowerCase() !== "success") {
            this.show_error(response.message || "Failed to save a question.");
            return;
          }

          await this.assign_questions_to_section(
            [response.data.question_id],
            this.section_id,
            payload.is_mandatory
          );

        } catch (error) {
          let message= error.response?.data?.message;
          if (typeof message === "string") {
            // Remove leading/trailing single or double quotes, if present
            message = message.replace(/^(["'])(.*)\1$/, "$2");
          }
          this.show_error(message || error.message);
          return;
        }
      }
      this.get_form_to_preview();
      this.question_forms = [this.create_empty_question_form()];
      this.show_success("All questions saved successfully!");
    },

    validate_form(form) {
      if (!form.question_text.trim()) return "Question text is required";
      if (!form.question_type_id) return "Question type is required";
      return null;
    },

    build_payload(form) {
      const type = this.question_types.find(t => t.id === form.question_type_id);
      const is_select_type = SELECT_TYPES.includes(type?.question_type);

      return {
        question_text: form.question_text.trim(),
        question_type_id: form.question_type_id,
        project_id: this.project_id,
        is_mandatory: form.mandatory === "Yes",
        question_number: form.question_number || null,
        multi_select_options: this.build_multi_select_options(is_select_type, form.option_inputs),
        section_id: this.section_id || null,
        data_point_id: form.data_point_id || null,
        tag_id: form.tag_id || null,
        dependant_questions: this.build_dependant_questions(form.dependant_questions),
      };
    },

    build_multi_select_options(is_select_type, option_inputs) {
      if (!is_select_type) return [];
      return option_inputs.filter(opt => Boolean(opt.trim()));
    },

    build_dependant_questions(dependant_questions) {
      if (!dependant_questions || dependant_questions.length === 0) return [];
      return dependant_questions.map(dep => {
        const dep_type = this.question_types.find(t => t.id === dep.question_type_id);
        const is_dep_select_type = SELECT_TYPES.includes(dep_type?.question_type);
        return {
          question_text: dep.question_text.trim(),
          question_type_id: dep.question_type_id,
          question_number: dep.question_number || null,
          is_mandatory: dep.is_mandatory || false,
          trigger_condition: dep.trigger_condition,
          data_point_id: dep.data_point_id || null,
          tag_id: dep.tag_id || null,
          multi_select_options: this.build_dependant_multi_select_options(is_dep_select_type, dep.options),
        };
      });
    },

    build_dependant_multi_select_options(is_dep_select_type, options) {
      if (!is_dep_select_type) return [];
      return options
        .filter(opt => Boolean(opt.choice?.trim()))
        .map(opt => opt.choice);
    },

    validate_payload(payload) {
      const type = this.question_types.find(t => t.id === payload.question_type_id);
      const is_select_type = SELECT_TYPES.includes(type?.question_type);

      if (is_select_type && payload.multi_select_options.length < 2) {
        this.show_error("At least two valid options are required");
        return false;
      }
      return true;
    },

    async submit_question(payload, form) {
      const res = await axios.post(CREATE_QUESTIONS_URLS, payload, {
        headers: this.headers,
      });

      const response = typeof res.data === "string" ? JSON.parse(res.data) : res.data;
      if (response.status?.toLowerCase() === "success") {
        await this.handle_success(response, payload, form);
      } else {
        this.handle_backend_error(response);
      }
    },

    async handle_success(response, payload, form) {
      this.show_success(response.message || "Question created successfully");

      const index = this.question_forms.indexOf(form);
      if (index !== -1) {
        this.question_forms.splice(index, 1, this.create_empty_question_form());
      }

      if (payload.section_id) {
        await this.assign_questions_to_section(
          [response.data.question_id],
          payload.section_id,
          payload.is_mandatory
        );
      }

      this.get_form_to_preview();
    },

    handle_backend_error(response) {
      const clean_message = this.extract_clean_error(response);
      this.show_error(clean_message);
    },

    handle_submission_error(error) {
      const backend_data = error?.response?.data || {};
      const clean_message = this.extract_clean_error(backend_data);
      this.show_error(clean_message);
    },

    extract_clean_error(response_data) {
      if (!response_data) return "Unknown error occurred";

      let messages = [];

      if (typeof response_data === "string") {
        messages = [response_data];
      } else if (Array.isArray(response_data)) {
        messages = response_data;
      } else if (typeof response_data === "object") {
        Object.values(response_data).forEach(value => {
          if (Array.isArray(value)) messages.push(...value);
          else if (typeof value === "string") messages.push(value);
        });
      }

      const message = messages.join(" ").trim();
      return message.replace(/^error[:\s]*/i, "").replace(/^['"]+|['"]+$/g, "");
    },



    save_edited_question() {
      if (!this.current_edit_question?.id) {
        return this.show_error('Question ID missing');
      }
      if (!this.current_edit_question.question_text?.trim()) {
        return this.show_error('Question text required');
      }
      if (!this.current_edit_question.question_type_id) {
        return this.show_error('Question type required');
      }

      const payload = {
        question_id: this.current_edit_question.id,
        question_text: this.current_edit_question.question_text.trim(),
        question_description: this.current_edit_question.question_description,
        question_type_id: this.current_edit_question.question_type_id,
        options: this.current_edit_question.show_option_inputs
          ? this.current_edit_question.options
            .map(opt => typeof opt === 'object' ? opt.choice : opt)
            .filter(choice => choice?.trim())
          : [],
        data_point_id: this.current_edit_question.data_point_id || null,
        tag_id: this.current_edit_question.selected_tag || null,
      };

      if (this.current_edit_question.show_option_inputs && payload.options.length < 2) {
        return this.show_error('At least two valid options required');
      }

      axios.put(EDIT_QUESTIONS_URL, payload, { headers: this.headers })
        .then(({ data }) => {
          const response = typeof data === "string" ? JSON.parse(data) : data;

          if (response.status === "success") {
            this.show_success('Question updated');
            this.show_edit_modal = false;
            this.get_form_to_preview();
            // this.sub_active_tab = "Preview Form";
          } else {
            this.show_error(response.message || 'Update failed');
          }
        })
        .catch((error) => {
          this.show_error(`Update failed: ${error.response?.data?.message || error.message}`);
        });
    },

    async create_section() {
      if (!this.section_name.trim()) {
        this.section_name_error = true;
        return this.show_error("Section name required");
      }

      const payload = {
        project_id: this.project_id,
        form_id: this.form_id,
        section_name: this.section_name.trim(),
      };

      await axios.post(CREATE_SECTION_IN_FORM, payload,
        {
          headers: this.headers

        }).then(() => {
          this.show_success(`Section ${this.section_name} created`);
          this.section_name = "";
          this.section_name_error = false;
          this.get_sections();
        }).catch(error => {
          this.show_error(`Error creating section: ${error.response?.data?.message || error.message}`);
        });
    },

    save_edited_section() {
      if (!this.current_edit_section?.id) return this.show_error("Section ID missing");
      if (!this.current_edit_section.name?.trim()) {
        this.section_name_error = true;
        return this.show_error("Section name required");
      }

      const payload = {
        name: this.current_edit_section.name.trim(),
        section_id: this.current_edit_section.id
      };

      axios.put(EDIT_SECTION_URL, payload, { headers: this.headers })
        .then(({ data }) => {
          const response = typeof data === 'string' ? JSON.parse(data) : data;

          if (response.status === 'success') {
            Swal.fire('Success', response.message || 'Section updated', 'success');
            this.get_sections();
          } else {
            Swal.fire('Error', response.message || 'Update failed', 'error');
          }
        })
        .catch(error => {
          const error_data = error.response?.data;
          const error_msg = typeof error_data === 'string' ?
            (JSON.parse(error_data)?.message || error_data) :
            (error_data?.message || error_data?.detail || 'Update failed');

          Swal.fire('Error', error_msg, 'error');
        });
    },

    async delete_section(section_id) {
      const result = await Swal.fire({
        title: 'Delete Section?',
        text: 'This will also delete all questions in this section',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Delete'
      });

      if (!result.isConfirmed) return;

      try {
        const { data } = await axios.delete(DELETE_SECTION_URL, {
          data: { section_id },
          headers: this.headers
        });

        if (data?.status === 'success') {
          Swal.fire('Deleted!', data.message || 'Section deleted', 'success');
          this.get_sections();
        } else {
          Swal.fire('Error', data?.message || 'Deletion failed', 'error');
        }
      } catch (error) {
        Swal.fire('Error', error.response?.data?.message || 'Deletion failed', 'error');
      }
    },

    async assign_questions_to_section(question_ids, section_id, is_mandatory = false) {
      if (!question_ids?.length || !section_id || !is_mandatory) return null;

      try {
        const payload = {
          project_id: this.project_id,
          form_id: this.form_id,
          section_id,
          question_ids,
          is_mandatory: Boolean(is_mandatory),
        };

        const { data } = await axios.post(
          ASSIGN_QUESTIONS_TO_FORM_SECTION,
          payload,
          { headers: this.headers }
        );

        const response = typeof data === "string" ? JSON.parse(data) : data;
        if (response.success) {
          this.show_success("Question assigned to section");
          this.get_form_to_preview();
        }
        return response;
      } catch (error) {
        this.show_error(`Error assigning question: ${error.response?.data?.message || error.message}`);
        return null;
      }
    },

    handle_dependent_question_type_change(dep_question) {
      const type = this.question_types.find(t => t.id === dep_question.question_type_id);
      dep_question.show_option_inputs = SELECT_TYPES.includes(type?.question_type);

      if (dep_question.show_option_inputs) {
        if (!dep_question.options || dep_question.options.length < 2) {
          dep_question.options = [{ choice: "" }, { choice: "" }];
        }
      } else {
        dep_question.options = [];
      }
    },

    toggle_dependent_section(form) {
      form.show_dependent_section = !form.show_dependent_section;
      if (form.show_dependent_section && !form.dependant_questions) {
        form.dependant_questions = [];
      }
    },

    add_dependent_question(form) {
      if (!form.dependant_questions) {
        form.dependant_questions = [];
      }

      form.dependant_questions.push({
        id: Date.now(),
        question_text: "",
        question_type_id: "",
        data_point_id: null,
        tag_id: null,
        question_number: "",
        options: [{ choice: "" }, { choice: "" }],
        trigger_condition: "",
        is_mandatory: false,
        available_tags: [],
        show_option_inputs: false
      });
    },

    remove_dependent_question(form, index) {
      if (form.dependant_questions && form.dependant_questions.length > index) {
        form.dependant_questions.splice(index, 1);
      }
    },

    add_dependent_option(dep_question) {
      if (!dep_question.options) {
        dep_question.options = [{ choice: "" }, { choice: "" }];
      } else {
        dep_question.options.push({ choice: "" });
      }
    },

    remove_dependent_option(dep_question, index) {
      if (dep_question.options && dep_question.options.length > 2) {
        dep_question.options.splice(index, 1);
      }
    }
  }
}